import { useCallback, useState } from "react";
import { useApi } from "../lib/api.ts";
import { useAsync } from "../lib/useAsync.ts";
import { formatBytes } from "../lib/format.ts";
import { UsageBar } from "../components/UsageBar.tsx";
import { ErrorState, PageHeader, Spinner } from "../components/States.tsx";

export default function Billing() {
  const api = useApi();
  const fetcher = useCallback((signal: AbortSignal) => api.getUsage(signal), [
    api,
  ]);
  const { data, error, loading, reload } = useAsync(fetcher, [api]);
  const [busy, setBusy] = useState<"checkout" | "portal" | null>(null);

  async function upgrade() {
    if (busy) return;
    setBusy("checkout");
    try {
      const { url } = await api.startCheckout();
      window.location.href = url;
    } catch {
      setBusy(null);
      // TODO: surface a toast on failure.
    }
  }

  async function manage() {
    if (busy) return;
    setBusy("portal");
    try {
      const { url } = await api.openBillingPortal();
      window.location.href = url;
    } catch {
      setBusy(null);
    }
  }

  const isFree = data ? /free/i.test(data.plan) : false;

  return (
    <section>
      <PageHeader
        title="Billing & usage"
        subtitle="Your current plan and how much of it you're using."
      />

      {loading && <Spinner />}
      {error && <ErrorState error={error} onRetry={reload} />}

      {data && (
        <div className="billing-grid">
          <div className="card plan-card">
            <span className="card-eyebrow">Current plan</span>
            <div className="plan-name">{data.plan}</div>
            <div className="plan-actions">
              {isFree ? (
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={upgrade}
                  disabled={busy !== null}
                >
                  {busy === "checkout" ? "Redirecting…" : "Upgrade — $4 / seat / mo"}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn--ghost"
                  onClick={manage}
                  disabled={busy !== null}
                >
                  {busy === "portal" ? "Redirecting…" : "Manage billing"}
                </button>
              )}
            </div>
          </div>

          <div className="card usage-card">
            <span className="card-eyebrow">Usage</span>
            {data.max_sessions != null ? (
              <UsageBar
                label="Sessions"
                value={data.sessions}
                max={data.max_sessions}
                display={`${data.sessions.toLocaleString()} / ${data.max_sessions.toLocaleString()}`}
              />
            ) : (
              <div className="usagebar-head">
                <span className="usagebar-label">Sessions</span>
                <span className="usagebar-value">
                  {data.sessions.toLocaleString()} · unlimited
                </span>
              </div>
            )}
            {/* Storage is uncapped on every plan — shown for info, not as a limit. */}
            <div className="usagebar-head">
              <span className="usagebar-label">Storage used</span>
              <span className="usagebar-value">{formatBytes(data.bytes)}</span>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
